<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useFavoritesStore } from '@state/stores/favorites'
import { useMessages } from '@composables/use-messages'
import formatCount from '@utils/format-count'

const props = defineProps({
    mix: {
        type: Object,
        default: () => {},
    },
    artist: {
        type: Object,
        default: () => {},
    },
    show: {
        type: Object,
        default: () => {},
    },
    channel: {
        type: Object,
        default: () => {},
    },
    size: {
        type: String,
        default: 'small', // none, small, iconOnly, large
    },
    buttonType: {
        type: String,
        default: 'tag', // tag, transparent, primary
    },
    buttonTypeActive: {
        type: [String, Boolean],
        default: false,
    },
    displayCounter: {
        type: Boolean,
        default: true,
    },
    iconMain: {
        type: String,
        default: 'heart-filled',
    },
    iconAlt: {
        type: String,
        default: 'heart',
    },
})

const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const favoritesStore = useFavoritesStore()
const router = useRouter()
const getMessageText = useMessages()

const countCache = ref(0)
const isReady = ref(true)

const favCount = computed(() => props.mix && props.mix.favorite_count)

const totalCount = computed(() => props.mix && favCount.value + countCache.value)

const isLoggedIn = computed(() => authStore.loggedIn)

const isActive = computed(() => {
    if (props.mix) {
        return !!favoritesStore.mixesList.find(el => el.id === props.mix.id)
    } else if (props.artist) {
        return !!favoritesStore.artistsList.find(el => el.id === props.artist.id)
    } else if (props.show) {
        return !!favoritesStore.showsList.find(el => el.id === props.show.id)
    } else if (props.channel) {
        return !!favoritesStore.channelIds.find(el => el === props.channel.id)
    } else {
        return false
    }
})

const buttonTypeActiveEvaluated = computed(() => props.buttonTypeActive || props.buttonType)

function notify(messageKey, type) {
    appearanceStore.handleNotification({ message: getMessageText(messageKey), type })
}

function addMix() {
    favoritesStore
        .add({ type: 'mixes', id: props.mix.id })
        .then(_res => {
            notify('FAV_MIX_ADDED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function removeMix() {
    favoritesStore
        .remove({ type: 'mixes', id: props.mix.id })
        .then(_res => {
            notify('FAV_MIX_REMOVED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function addArtist() {
    favoritesStore
        .add({ type: 'artists', id: props.artist.id })
        .then(_res => {
            notify('FAV_ARTIST_ADDED', 'success')
        })
        .catch(_err => {
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function removeArtist() {
    favoritesStore
        .remove({ type: 'artists', id: props.artist.id })
        .then(_res => {
            notify('FAV_ARTIST_REMOVED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function addShow() {
    favoritesStore
        .add({ type: 'shows', id: props.show.id })
        .then(_res => {
            notify('FAV_SHOW_ADDED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function removeShow() {
    favoritesStore
        .remove({ type: 'shows', id: props.show.id })
        .then(_res => {
            notify('FAV_SHOW_REMOVED', 'success')
        })
        .catch(err => {
            console.error(err)
            notify('SOMETHING_WENT_WRONG', 'warning')
        })
}

function addChannel() {
    favoritesStore.toggleChannel(props.channel.id)
}

function removeChannel() {
    favoritesStore.toggleChannel(props.channel.id)
}

const add = computed(() => (props.mix ? addMix : props.artist ? addArtist : props.show ? addShow : addChannel))

const remove = computed(() =>
    props.mix ? removeMix : props.artist ? removeArtist : props.show ? removeShow : removeChannel
)

watch(isActive, () => {
    isActive.value ? countCache.value++ : countCache.value--
    isReady.value = true
})

function addRemoveFav() {
    if (isLoggedIn.value) {
        if (isReady.value) {
            isReady.value = false
            isActive.value ? remove.value() : add.value()
        }
    } else {
        router.push({ name: 'login' })
    }
}
</script>

<template>
    <BaseButton
        :active="isLoggedIn && isActive"
        :button-type="isActive ? buttonType : buttonTypeActiveEvaluated"
        :size="size"
        :class="[
            'base-favourites',
            buttonType === 'transparent' ? 'base-favourites-transparent' : '',
            isActive ? 'base-favourites-active' : '',
            (mix && mix.id) || (artist && artist.id) || (show && show.id) || (channel && channel.id)
                ? ''
                : 'base-favourites-inactive',
        ]"
        :icon="size === 'large' ? false : isActive ? iconMain : iconAlt"
        @click="addRemoveFav"
    >
        <slot v-if="!$slots['main'] && displayCounter && size !== 'none'">{{
            size !== 'icon-only' ? formatCount(totalCount > 0 ? totalCount : isActive ? 1 : 0) : ''
        }}</slot>
        &nbsp;
        <slot
            v-if="!isActive"
            name="main"
        />
        <slot
            v-else
            name="alt"
        />
    </BaseButton>
</template>

<style lang="scss" scoped>
@import '@design';

.base-favourites {
    &-transparent {
        &:hover :deep(.icon),
        &.base-favourites-active :deep(.icon) {
            color: $c-accent-default;
        }
    }
}

.base-favourites-inactive {
    pointer-events: none;
    opacity: 0.3;
    filter: grayscale(1);
}
</style>
